import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import { faChevronRight, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { fetchMessageCheckoutDetails } from 'api/engage';
import { RecipientCountButton } from 'assets/js/pages/engage/letters/RecipientCountButton';
import { POSTAGE_TYPES } from 'assets/js/pages/engage/letters/constants';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useLetterRecipientContactFilter } from 'assets/js/pages/engage/letters/useLetterRecipientContactFilter';
import * as ReadOnlyPaymentDetails from 'assets/js/pages/transactions/ReadOnlyPaymentDetails';
import { TransactionMethodPresentational } from 'assets/js/pages/transactions/TransactionMethod';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { PolymorphicTransaction } from 'types/domain';

const INVOICE_REFETCH_INTERVAL = 5000;

const LetterEasySendOrderDetails = () => {
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const recipientsOnClick = useLetterRecipientContactFilter();
    const { data: checkoutDetails } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageCheckoutDetails(accountId, letter.id),
        queryFn: () => fetchMessageCheckoutDetails(accountId, letter.id),
        refetchInterval: query => {
            if (query.state.status === 'error') {
                return false;
            }
            const invoiceUrl = query.state.data?.invoice_url;
            return invoiceUrl ? false : INVOICE_REFETCH_INTERVAL;
        },
    });

    const paymentReceivedAt = letter.payment_received_at
        ? isoDateFromServer(letter.payment_received_at)
        : null;
    const letterSendingAt = letter.sending_at ? isoDateFromServer(letter.sending_at) : null;

    const [firstName, lastName = ''] = (checkoutDetails.name ?? '').split(' ');
    const composedTransaction = {
        payment_method: {
            ...checkoutDetails.payment_method,
            method: 'card',
        },
        address: checkoutDetails.billing_address,
        phone: checkoutDetails.phone,
        email: checkoutDetails.email,
        first_name: firstName,
        last_name: lastName,
    } as unknown as PolymorphicTransaction;

    return (
        <>
            {letter.status === 'failed' && (
                <AlertNotification.Root type={'error'}>
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            Letters failed to generate
                        </AlertNotification.Title>
                        <AlertNotification.Description>
                            An issue occurred while generating your letters. Please contact support
                            by clicking the chat icon in the top right corner.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                    <AlertNotification.Close />
                </AlertNotification.Root>
            )}
            <Box className={'flex flex-col gap-4 p-6'}>
                <p className={'text-xl font-medium'}>{letter.internal_name}</p>
                <Separator className={'-mx-6 !w-auto'} />
                <div className={'flex flex-wrap gap-8'}>
                    <div className={'flex flex-1 flex-col gap-2'}>
                        <p className={'text-sm font-medium'}>
                            Order{' '}
                            {checkoutDetails.order_number
                                ? `#${checkoutDetails.order_number}`
                                : 'processing'}
                        </p>
                        <div className={'flex flex-col gap-4'}>
                            <div className={'flex flex-col gap-2 rounded-md bg-gray-50 p-2'}>
                                <div className={'flex justify-between gap-2'}>
                                    <p className={'text-sm font-medium text-gray-800'}>
                                        {capitalize(letter.settings.page_type)} &times;{' '}
                                        {checkoutDetails.quantity}
                                    </p>
                                    <p className={'text-sm font-medium text-gray-600'}>
                                        {checkoutDetails.total_amount}
                                    </p>
                                </div>
                                <p className={'text-sm text-gray-600'}>
                                    {checkoutDetails.unit_price}/letter
                                </p>
                                <Separator className={'-mx-2 !w-auto'} />
                                <div className={'flex flex-col gap-2'}>
                                    {checkoutDetails.tax_amount &&
                                        checkoutDetails.tax_amount !== '$0.00' && (
                                            <div className={'flex justify-between gap-2'}>
                                                <p className={'text-sm font-medium text-gray-800'}>
                                                    Sales tax
                                                </p>
                                                <p className={'text-sm font-medium text-gray-600'}>
                                                    {checkoutDetails.tax_amount}
                                                </p>
                                            </div>
                                        )}
                                    <div className={'flex justify-between gap-2'}>
                                        <p className={'font-medium text-gray-900'}>Total</p>
                                        <p className={'font-medium text-gray-900'}>
                                            {checkoutDetails.total_amount}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {checkoutDetails.invoice_url ? (
                                <div className="w-fit">
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={checkoutDetails.invoice_url}
                                    >
                                        <Button
                                            size={'sm'}
                                            className={'flex w-fit items-center gap-1.5 self-end'}
                                            variant={'outline'}
                                            color={'neutral'}
                                        >
                                            <p>Download receipt</p>
                                            <FontAwesomeIcon
                                                className={'text-gray-300'}
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </a>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                    <Loader size="sm" className="relative" />
                                    Generating receipt
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={'flex flex-1 flex-col gap-2'}>
                        <div className={'flex justify-between gap-2'}>
                            <p className={'text-sm font-medium text-gray-900'}>Type</p>
                            <p className={'text-sm text-gray-600'}>
                                {capitalize(letter.settings.page_type)}
                            </p>
                        </div>
                        <div className={'flex justify-between gap-2'}>
                            <p className={'text-sm font-medium text-gray-900'}>Recipients</p>
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild={true}>
                                    <RecipientCountButton
                                        className="text-sm"
                                        onClick={recipientsOnClick}
                                        noExcluded={true}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>View in Contacts</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </div>
                        <div className={'flex justify-between gap-2'}>
                            <p className={'text-sm font-medium text-gray-900'}>Shipping speed</p>
                            <p className={'text-sm text-gray-600'}>
                                {POSTAGE_TYPES[letter.settings.postage_type].label}
                            </p>
                        </div>
                        {paymentReceivedAt && (
                            <div className={'flex justify-between gap-2'}>
                                <p className={'text-sm font-medium text-gray-900'}>Date ordered</p>
                                <p className={'text-sm text-gray-600'}>
                                    {paymentReceivedAt.toLocaleString(DateTime.DATETIME_MED)}{' '}
                                    {paymentReceivedAt.offsetNameShort}
                                </p>
                            </div>
                        )}
                        <div className={'flex justify-between gap-2'}>
                            <p className={'text-sm font-medium text-gray-900'}>Paper settings</p>
                            <p className={'text-sm text-gray-600'}>
                                {letter.settings.is_double_sided ? 'Double-sided' : 'Single-sided'}
                            </p>
                        </div>
                        {letterSendingAt && (
                            <div className={'flex justify-between gap-2'}>
                                <p className={'text-sm font-medium text-gray-900'}>Process date</p>
                                <p className={'text-sm text-gray-600'}>
                                    {letterSendingAt.toLocaleString(DateTime.DATE_MED)}{' '}
                                    {letterSendingAt.offsetNameShort}
                                </p>
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            <p className={'text-sm font-medium text-gray-900'}>Payment details</p>
                            <div className={`flex items-center gap-2 text-gray-600`}>
                                <Collapsible.Root className={'group w-full'}>
                                    <Collapsible.Trigger>
                                        <div className={'flex items-center gap-2'}>
                                            <FontAwesomeIcon
                                                className={
                                                    'group-radix-state-open:rotate-90 text-gray-600'
                                                }
                                                icon={faChevronRight}
                                            />
                                            <TransactionMethodPresentational
                                                transaction={composedTransaction}
                                            />
                                        </div>
                                    </Collapsible.Trigger>
                                    <Collapsible.Content className={'bg-gray-25 rounded-lg p-3'}>
                                        <ReadOnlyPaymentDetails.Root
                                            transaction={composedTransaction}
                                        >
                                            <ReadOnlyPaymentDetails.Name />
                                            <ReadOnlyPaymentDetails.Phone />
                                            <ReadOnlyPaymentDetails.Address />
                                            <ReadOnlyPaymentDetails.Email />
                                            <ReadOnlyPaymentDetails.Method />
                                            <ReadOnlyPaymentDetails.CcExpire />
                                        </ReadOnlyPaymentDetails.Root>
                                    </Collapsible.Content>
                                </Collapsible.Root>
                            </div>
                        </div>
                    </div>
                </div>
            </Box>
        </>
    );
};

export { LetterEasySendOrderDetails };
